import { Badge, Box, SegmentedControl, type SegmentedControlOption } from 'pixelize-design-library';

import { accentText, mutedText } from '@core/Theme/tokens';
import { useAppTheme } from '@core/Theme/useAppTheme';

export interface CountTabOption<T extends string = string> {
  value: T;
  label: string;
  count?: number;
}

export interface CountTabsProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: CountTabOption<T>[];
  'aria-label': string;
}

const SR_ONLY = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap'
} as const;

export function CountTabs<T extends string = string>({
  value,
  onChange,
  options,
  'aria-label': ariaLabel
}: CountTabsProps<T>) {
  const { colors } = useAppTheme();
  const segments: SegmentedControlOption<T>[] = options.map((option) => {
    const selected = option.value === value;
    return {
      value: option.value,
      label: option.label,
      icon:
        option.count === undefined ? undefined : (
          <Box as='span' order={1} display='inline-flex'>
            <Box as='span' sx={SR_ONLY}>{`${option.count} `}</Box>
            <Badge
              aria-hidden
              borderRadius='full'
              px={2}
              fontSize='xs'
              bg={selected ? colors.primary[50] : colors.gray[100]}
              color={selected ? accentText(colors) : mutedText(colors)}
            >
              {option.count}
            </Badge>
          </Box>
        )
    };
  });
  return (
    <Box maxW='100%' overflowX='auto' overflowY='hidden'>
      <SegmentedControl<T>
        options={segments}
        value={value}
        onChange={onChange}
        size='sm'
        variant='underlineSlide'
        aria-label={ariaLabel}
      />
    </Box>
  );
}
